import cx from 'classnames';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router-dom';
import { P, Span } from '../../../typography';
import { AuthConsumer, User } from '../../../containers/shared/auth/AuthContext';
import Can from '../../../containers/shared/auth/Can';
import { getArrayFromObject } from '../../../utils';
import {
  LetterAvatar,
  NavItemWrapper,
  StyledCollapse,
  StyledExpand,
  StyledLink,
  StyledSubMenuLink,
  Tooltip,
} from './SideBarLinkElements';
import { isPathUnderPrefix } from './activePath';
import UpgradeDiamond from '../UpgradeDiamond';

export type SideBarLinkProps = RouteComponentProps & {
  to: string;
  isWeb?: boolean;
  bg?: string;
  open: boolean;
  title: string;
  color?: string;
  onClick?: () => void;
  pathname?: string;
  hasSubMenu?: boolean;
  icon?: React.ReactNode;
  location?: { pathname?: string };
  item?: {
    to: string;
    title: string;
    check?: (user: User, hasCaseManagement: boolean) => boolean;
    locked?: (user: User) => boolean;
  };
  map?: (item: object) => React.ReactNode;
  activePathPrefix?: string;
  subMenu?: Array<{
    to: string;
    title: string;
    check?: (user: User, hasCaseManagement: boolean) => boolean;
    locked?: (user: User) => boolean;
  }>;
};

class SideBarLink extends React.Component<SideBarLinkProps, {}> {
  static defaultProps = {
    backgroundColor: 'transparent',
    color: '#FFF',
  };

  state = {
    expanded: false,
  };

  componentDidMount() {
    const { subMenu, location } = this.props;

    if (location) {
      if (getArrayFromObject(subMenu).some(path => path.to === location.pathname)) {
        // we are in the this route so we open submenu.
        this.setState({ expanded: true });
      }
    }
  }

  _isChildActive() {
    const { subMenu, location } = this.props;
    if (location) {
      return getArrayFromObject(subMenu).some(path => path.to === location.pathname);
    }
    return undefined;
  }

  _isActive() {
    const { activePathPrefix, location } = this.props;
    return this._isChildActive() || isPathUnderPrefix(location && location.pathname, activePathPrefix);
  }

  toggleExpanded = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    const { subMenu } = this.props;
    if (!!subMenu) {
      this.setState({
        expanded: !this.state.expanded,
      });
    }
  };

  _renderIcon = (icon: React.ReactNode) => {
    if (React.isValidElement(icon)) {
      return icon;
    }
    return null;
  };

  _getLetters(title: string) {
    let matches = title.match(/\b(\w)/g);
    if (matches) {
      if (matches.length < 3) {
        return matches.join('');
      } else {
        const sliced = matches.slice(1, 3);
        return sliced.join('');
      }
    }
    return '';
  }

  _renderSubMenu = (
    subMenu: Array<{
      to: string;
      title: string;
      check?: (user: User, hasCaseManagement: boolean) => boolean;
      locked?: (user: User) => boolean;
    }>,
    { ...props }
  ) => {
    return subMenu
      .filter(
        (i: {
          to: string;
          title: string;
          check?: (user: User, hasCaseManagement: boolean) => boolean;
          locked?: (user: User) => boolean;
        }) => i.title
      )
      .map(
        (
          item: {
            to: string;
            title: string;
            check?: (user: User, hasCaseManagement: boolean) => boolean;
            locked?: (user: User) => boolean;
          },
          index
        ) => (
          <Can
            logout={props.logout}
            landingPage={false}
            key={`submenu-${index}`}
            roles={props.user.roles}
            plan={props.user.plan}
            userAddOns={props.user.addOns}
            navItemCheck={item.check ? item.check(props.user, props.hasCaseManagement) : undefined}
            action={`${item.to}:visit`}
            yes={() => (
              <StyledSubMenuLink
                to={item.to}
                activeClassName="active-submenu-link"
                className={props.open ? 'nav-link' : 'nav-link-text-hidden'}
              >
                <NavItemWrapper open={props.open} isWeb={this.props.isWeb}>
                  {props.open ? (
                    <Span
                      pointer={true}
                      ml="60px"
                      color="#FFF"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      {item.title}
                      {item.locked && item.locked(props.user) ? <UpgradeDiamond size={12} invert={true} /> : null}
                    </Span>
                  ) : (
                    <LetterAvatar open={props.open}>
                      <P className="abbreviated-letters">{this._getLetters(item.title)}</P>
                      <Tooltip>
                        <P p={0} m={0}>
                          {item.title}
                        </P>
                      </Tooltip>
                    </LetterAvatar>
                  )}
                </NavItemWrapper>
              </StyledSubMenuLink>
            )}
            no={() => null}
          />
        )
      );
  };

  render() {
    const { icon, to, isWeb, title, open, color, subMenu, bg, onClick } = this.props;
    const { expanded } = this.state;
    const hasSubMenu = !!subMenu;

    return (
      <React.Fragment>
        <StyledLink
          to={to}
          open={this.state.expanded}
          onClick={hasSubMenu ? this.toggleExpanded : () => null}
          className={cx('nav-link', { 'nav-link-text-hidden': !open }, { 'child-link-active': this._isActive() })}
        >
          <NavItemWrapper open={open} bg={bg} isWeb={isWeb}>
            {this._renderIcon(icon)}
            <Span pointer={true} ml="20px" color={color} onClick={onClick}>
              {title}
            </Span>
            {subMenu && <StyledExpand size={20} open={open} expanded={expanded.toString()} />}
            {!open && (
              <Tooltip>
                <P>{title}</P>
              </Tooltip>
            )}
          </NavItemWrapper>
        </StyledLink>

        {/* Submenus */}
        <AuthConsumer>
          {({ user, logout, hasCaseManagement }) => {
            return (
              <StyledCollapse
                active={this._isChildActive()}
                open={expanded}
                sidebarOpen={open}
                allowOverflow={expanded}
              >
                {subMenu &&
                  typeof subMenu !== undefined &&
                  this._renderSubMenu(subMenu, { open, color, user, logout, hasCaseManagement })}
              </StyledCollapse>
            );
          }}
        </AuthConsumer>
      </React.Fragment>
    );
  }
}

export default withRouter(SideBarLink);
